// Zadanie zamkniete E8: laczenie grup o podanych srednich arytmetycznych.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function formatNumber(value) {
  return Number.isInteger(value)
    ? `${value}`
    : value.toFixed(1).replace(".", "{,}");
}

function createOriginalTask() {
  return {
    type: "E8CombinedMeansClosedTask",
    displayBaseNumber: 4,
    data: {
      firstCount: 4,
      secondCount: 2,
      firstMean: 9,
      secondMean: 6,
      firstSum: 36,
      secondSum: 12,
      sumDifference: 24,
      combinedMean: 8,
      firstOptions: [
        { label: "A", value: 3 },
        { label: "B", value: 24 },
      ],
      secondOptions: [
        { label: "C", value: 8 },
        { label: "D", value: 7.5 },
      ],
      firstCorrectIndex: 1,
      secondCorrectIndex: 0,
    },
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 4,
    },
    tags: ["e8", "statystyka", "srednia_arytmetyczna"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  for (let attempt = 0; attempt < 200; attempt += 1) {
    const firstCount = 4;
    const secondCount = 2;
    const firstMean = pick([7, 8, 9, 10, 11, 12, 13, 14]);
    const secondMean = pick([3, 4, 5, 6, 7, 8]);
    const firstSum = firstCount * firstMean;
    const secondSum = secondCount * secondMean;
    const sumDifference = firstSum - secondSum;
    const meanDifference = firstMean - secondMean;
    const combinedMean =
      (firstSum + secondSum) / (firstCount + secondCount);
    const unweightedMean = (firstMean + secondMean) / 2;

    if (
      sumDifference <= 0 ||
      meanDifference <= 0 ||
      sumDifference === meanDifference ||
      combinedMean === unweightedMean
    ) {
      continue;
    }

    const firstCorrectFirst = Math.random() < 0.5;
    const secondCorrectFirst = Math.random() < 0.5;
    const firstValues = firstCorrectFirst
      ? [sumDifference, meanDifference]
      : [meanDifference, sumDifference];
    const secondValues = secondCorrectFirst
      ? [combinedMean, unweightedMean]
      : [unweightedMean, combinedMean];

    return {
      type: "E8CombinedMeansClosedTask",
      displayBaseNumber: 4,
      data: {
        firstCount,
        secondCount,
        firstMean,
        secondMean,
        firstSum,
        secondSum,
        sumDifference,
        combinedMean,
        firstOptions: firstValues.map((value, index) => ({
          label: index === 0 ? "A" : "B",
          value,
        })),
        secondOptions: secondValues.map((value, index) => ({
          label: index === 0 ? "C" : "D",
          value,
        })),
        firstCorrectIndex: firstCorrectFirst ? 0 : 1,
        secondCorrectIndex: secondCorrectFirst ? 0 : 1,
      },
      source: { variant: "random", basedOn: "E8_2025_MAJ_4" },
      tags: ["e8", "statystyka", "srednia_arytmetyczna"],
    };
  }

  return {
    ...createOriginalTask(),
    source: { variant: "random", basedOn: "E8_2025_MAJ_4" },
  };
}

function ChoiceBox({ labels }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        border: "1.5px solid currentColor",
        borderRadius: 4,
        padding: "1px 8px",
        margin: "0 4px",
        opacity: 0.75,
        verticalAlign: "middle",
        fontSize: "0.9em",
      }}
    >
      {labels.map((label, index) => (
        <React.Fragment key={label}>
          {index > 0 && <span style={{ opacity: 0.5 }}>|</span>}
          <strong>
            <InlineMath math={label} />
          </strong>
        </React.Fragment>
      ))}
    </span>
  );
}

function NoBreakMath({ math }) {
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <InlineMath math={math} />
    </span>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Średnia arytmetyczna czterech liczb{" "}
        <NoBreakMath math="a, b, c, d" /> jest równa{" "}
        <InlineMath math={formatNumber(data.firstMean)} />, a średnia
        arytmetyczna dwóch liczb <InlineMath math="e" /> i{" "}
        <InlineMath math="f" /> jest równa{" "}
        <InlineMath math={formatNumber(data.secondMean)} />.
      </p>
      <p>
        <strong>
          Uzupełnij zdania. Wybierz odpowiedź spośród oznaczonych literami{" "}
          <InlineMath math="A" /> i <InlineMath math="B" /> oraz odpowiedź
          spośród oznaczonych literami <InlineMath math="C" /> i{" "}
          <InlineMath math="D" />.
        </strong>
      </p>
      <p>
        Suma liczb <NoBreakMath math="a, b, c, d" /> jest o{" "}
        <ChoiceBox labels={["A", "B"]} /> większa od sumy liczb{" "}
        <InlineMath math="e" /> i <InlineMath math="f" />.
      </p>
      <p style={{ marginLeft: 16 }}>
        {data.firstOptions.map((option, index) => (
          <React.Fragment key={option.label}>
            {index > 0 && <span style={{ marginLeft: 36 }} />}
            <ChoiceOptionMath letter={option.label} math={formatNumber(option.value)} />
          </React.Fragment>
        ))}
      </p>
      <p>
        Średnia arytmetyczna liczb{" "}
        <NoBreakMath math="a, b, c, d, e, f" /> jest równa{" "}
        <ChoiceBox labels={["C", "D"]} />.
      </p>
      <p style={{ marginLeft: 16 }}>
        {data.secondOptions.map((option, index) => (
          <React.Fragment key={option.label}>
            {index > 0 && <span style={{ marginLeft: 36 }} />}
            <ChoiceOptionMath letter={option.label} math={formatNumber(option.value)} />
          </React.Fragment>
        ))}
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const answer = userAnswer || {};
  return (
    <div style={{ display: "grid", rowGap: 14, marginTop: 10 }}>
      <div>
        <div style={{ marginBottom: 4, fontSize: 13, opacity: 0.7 }}>
          Wybierz <InlineMath math="A" /> lub <InlineMath math="B" />:
        </div>
        <ABCDInput
          options={[
            { value: 0, label: "A", textNode: <InlineMath math="A" /> },
            { value: 1, label: "B", textNode: <InlineMath math="B" /> },
          ]}
          value={answer.firstChoice ?? null}
          onChange={(firstChoice) => onChange({ ...answer, firstChoice })}
          disabled={disabled}
        />
      </div>
      <div>
        <div style={{ marginBottom: 4, fontSize: 13, opacity: 0.7 }}>
          Wybierz <InlineMath math="C" /> lub <InlineMath math="D" />:
        </div>
        <ABCDInput
          options={[
            { value: 0, label: "C", textNode: <InlineMath math="C" /> },
            { value: 1, label: "D", textNode: <InlineMath math="D" /> },
          ]}
          value={answer.secondChoice ?? null}
          onChange={(secondChoice) => onChange({ ...answer, secondChoice })}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const firstCorrect = userAnswer?.firstChoice === data.firstCorrectIndex;
  const secondCorrect = userAnswer?.secondChoice === data.secondCorrectIndex;
  return {
    isCorrect: firstCorrect && secondCorrect,
    correct: firstCorrect && secondCorrect,
    score: firstCorrect && secondCorrect ? 1 : 0,
    maxScore: 1,
  };
}

export function renderAnswer({ data }) {
  const first = data.firstOptions[data.firstCorrectIndex];
  const second = data.secondOptions[data.secondCorrectIndex];
  return (
    <span>
        <ChoiceOptionMath letter={first.label} math={formatNumber(first.value)} />
        {", "}
        <ChoiceOptionMath letter={second.label} math={formatNumber(second.value)} />
    </span>
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  const first = data.firstOptions[userAnswer?.firstChoice];
  const second = data.secondOptions[userAnswer?.secondChoice];
  if (!first && !second) return <i>(brak odpowiedzi)</i>;
  return (
    <span>
      {first ? (
        <ChoiceOptionMath letter={first.label} math={formatNumber(first.value)} />
      ) : (
        "—"
      )}
      {", "}
      {second ? (
        <ChoiceOptionMath letter={second.label} math={formatNumber(second.value)} />
      ) : (
        "—"
      )}
    </span>
  );
}

export function renderExplanation({ data }) {
  const first = data.firstOptions[data.firstCorrectIndex];
  const second = data.secondOptions[data.secondCorrectIndex];
  const totalCount = data.firstCount + data.secondCount;
  const totalSum = data.firstSum + data.secondSum;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Obliczamy sumę pierwszych czterech liczb.
      </p>
      <BlockMath
        math={`a+b+c+d=${data.firstCount}\\cdot${formatNumber(
          data.firstMean
        )}=${data.firstSum}`}
      />
      <p>
        <strong>Krok 2.</strong> Obliczamy sumę dwóch pozostałych liczb.
      </p>
      <BlockMath
        math={`e+f=${data.secondCount}\\cdot${formatNumber(
          data.secondMean
        )}=${data.secondSum}`}
      />
      <p>
        <strong>Krok 3.</strong> Porównujemy obie sumy.
      </p>
      <BlockMath
        math={`${data.firstSum}-${data.secondSum}=${data.sumDifference}`}
      />
      <p>
        Pierwsza poprawna odpowiedź to{" "}
        <strong>
        <ChoiceOptionMath letter={first.label} math={formatNumber(first.value)} />
        </strong>
        .
      </p>
      <p>
        <strong>Krok 4.</strong> Dodajemy sumy obu grup i dzielimy przez liczbę
        wszystkich elementów.
      </p>
      <BlockMath
        math={`\\frac{${data.firstSum}+${data.secondSum}}{${data.firstCount}+${data.secondCount}}=\\frac{${totalSum}}{${totalCount}}=${formatNumber(
          data.combinedMean
        )}`}
      />
      <p>
        Nie obliczamy zwykłej średniej z liczb{" "}
        <InlineMath
          math={`${formatNumber(data.firstMean)}\\ \\text{i}\\ ${formatNumber(
            data.secondMean
          )}`}
        />
        , ponieważ grupy mają różną liczebność.
      </p>
      <p>
        <strong>Krok 5.</strong> Druga poprawna odpowiedź to{" "}
        <strong>
        <ChoiceOptionMath letter={second.label} math={formatNumber(second.value)} />
        </strong>
        .
      </p>
    </div>
  );
}
